import Grid from '@mui/material/Grid';
import { Button } from '@openx/components-core/lib/Button/Button.tsx';
import { StatusIndicator } from '@openx/components-core/lib/StatusIndicator/StatusIndicator.tsx';
import type { Audience } from '@openx/types/targeting/audience.ts';
import { useCallback } from 'react';
import { type FieldValues, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { AudienceExpirationAlerts } from '../../shared/AudienceExpirationAlerts/AudienceExpirationAlerts.tsx';
import { Item } from '../../shared/SelectedItems/Item.tsx';
import type { SelectedItemComponent } from '../../shared/SelectedItems/Items.tsx';
import { createRedirectPath } from '../utils/createAudienceNavigationState.ts';

export const OaAudienceItem: SelectedItemComponent = props => {
  const { t } = useTranslation();
  const { children, option, testId, isMinimalAudiencePreview } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const formContext = useFormContext<FieldValues>();

  const audienceId = option?.id.replace('openaudience-', '');
  const audienceHref = `/audiences/${audienceId}${isMinimalAudiencePreview ? '/read-only' : ''}?fallback=true`;

  const handleShowAudience = useCallback(() => {
    const formDataKey = location.pathname.startsWith('/inventory/packages')
      ? 'package'
      : location.pathname.startsWith('/deals')
        ? 'deal'
        : null;
    const formValues = formContext?.getValues();

    navigate(audienceHref, {
      state: {
        ...(location.state ?? {}),
        ...(formDataKey && formValues ? { [formDataKey]: formValues } : {}),
        redirectPath: createRedirectPath(location),
      },
    });
  }, [audienceHref, formContext, location, navigate]);

  return (
    <Grid
      wrap="nowrap"
      container
      sx={{
        gap: 1,
        flexGrow: 1,
        justifyContent: 'space-between',
      }}
    >
      <Grid
        sx={{
          paddingRight: 2,
        }}
      >
        <Item {...props}>{children}</Item>
      </Grid>
      <Grid
        size={2}
        sx={{
          paddingTop: 0.25,
        }}
      >
        <Grid
          container
          sx={{
            justifyContent: 'flex-end',
          }}
        >
          <StatusIndicator status={option?.status} data-test={testId?.replace('-item', '-status')} />
        </Grid>
      </Grid>

      <Grid size={1}>
        {option && (
          <AudienceExpirationAlerts
            audience={option as Audience}
            fromDeal={true}
            sx={{ height: 28, marginRight: 0.5 }}
          />
        )}
      </Grid>

      {audienceId && (
        <Grid size={3}>
          <Grid
            container
            sx={{
              justifyContent: 'flex-end',
            }}
          >
            <Button data-test="show-audience-button" variant="text" color="primary" onClick={handleShowAudience}>
              {t('SHOW AUDIENCE')}
            </Button>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};
